import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  DrawerBody,
  DrawerHeader,
  DrawerHeaderTitle,
  OverlayDrawer,
  mergeClasses,
} from '@fluentui/react-components';
import { Dismiss24Regular } from '@fluentui/react-icons';
import { BasicButton } from '@components/common/BasicButton';
import { useRailVisibleItems } from '@/hooks/useRailVisibleItems';
import { telemetry } from '@services/telemetry';
import { TELEMETRY_COMPONENT, EVENT_TYPES, TELEMETRY_PROPERTIES } from '@/constants';
import type { RailItem, RailItemViewModel, RailSubItem } from '@/types/leftNavRail.types';
import { useMobileNavDrawerStyles } from './MobileNavDrawer.styles';

export interface MobileNavDrawerProps {
  /** Controlled open state of the drawer. */
  isOpen: boolean;
  /**
   * Called when the drawer requests dismissal (Escape, outside-tap, close
   * button, item activation). The parent owns the open state.
   */
  onDismiss: () => void;
  /** Optional override for test purposes. Defaults to `useRailVisibleItems()`. */
  items?: readonly RailItemViewModel[];
  /** Optional override of the title text. Defaults to the app title key. */
  title?: string;
}

type CloseSource = 'CloseButton' | 'Navigate' | 'Escape' | 'OutsideTap';

const PREFERS_REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

function useReducedMotion(): boolean {
  const [reduced, setReduced] = useState<boolean>(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
    return window.matchMedia(PREFERS_REDUCED_MOTION_QUERY).matches;
  });
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
    const mql = window.matchMedia(PREFERS_REDUCED_MOTION_QUERY);
    const handler = (event: MediaQueryListEvent) => setReduced(event.matches);
    setReduced(mql.matches);
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, []);
  return reduced;
}

function emitClose(source: CloseSource): void {
  telemetry.trackEvent(TELEMETRY_COMPONENT, EVENT_TYPES.BUTTON_CLICK, {
    [TELEMETRY_PROPERTIES.BUTTON_NAME]: 'Header.MobileNav.Close',
    source,
  });
}

export function MobileNavDrawer({
  isOpen,
  onDismiss,
  items: itemsOverride,
  title,
}: MobileNavDrawerProps): React.JSX.Element {
  const { t } = useTranslation();
  const styles = useMobileNavDrawerStyles();
  const navigate = useNavigate();
  const reducedMotion = useReducedMotion();
  const closeSourceRef = useRef<CloseSource | null>(null);

  const railItems = useRailVisibleItems();
  const items = itemsOverride ?? railItems.items;

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        closeSourceRef.current = 'Escape';
      }
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [isOpen]);

  const dismiss = useCallback(
    (source: CloseSource) => {
      closeSourceRef.current = source;
      emitClose(source);
      onDismiss();
    },
    [onDismiss]
  );

  const handleOpenChange = useCallback(
    (_event: unknown, data: { open: boolean }) => {
      if (data.open) return;
      const source = closeSourceRef.current ?? 'OutsideTap';
      closeSourceRef.current = null;
      if (source !== 'CloseButton' && source !== 'Navigate') {
        emitClose(source);
      }
      onDismiss();
    },
    [onDismiss]
  );

  const handleCloseClick = useCallback(() => {
    dismiss('CloseButton');
  }, [dismiss]);

  const handleNavigate = useCallback(
    (vm: RailItemViewModel) => {
      telemetry.trackEvent(TELEMETRY_COMPONENT, EVENT_TYPES.NAVIGATION, {
        source: 'mobileNavDrawer',
        destination: vm.item.key,
        target: vm.item.defaultRoute,
      });
      void navigate(vm.item.defaultRoute);
      dismiss('Navigate');
    },
    [dismiss, navigate]
  );

  const handleNavigateSub = useCallback(
    (parent: RailItem, sub: RailSubItem) => {
      telemetry.trackEvent(TELEMETRY_COMPONENT, EVENT_TYPES.NAVIGATION, {
        source: 'mobileNavDrawer',
        destination: `${parent.key}.${sub.key}`,
        target: sub.route,
      });
      void navigate(sub.route);
      dismiss('Navigate');
    },
    [dismiss, navigate]
  );

  if (!isOpen) {
    return <></>;
  }

  const drawerTitle = title ?? t('header.appTitle');

  return (
    <OverlayDrawer
      open={isOpen}
      onOpenChange={handleOpenChange}
      position="start"
      size="small"
      data-testid="mobile-nav-drawer"
      data-reduced-motion={reducedMotion ? 'true' : 'false'}
      className={styles.surface}
    >
      <DrawerHeader>
        <DrawerHeaderTitle
          action={
            <BasicButton
              variant="subtle"
              icon={<Dismiss24Regular />}
              aria-label={t('leftNav.mobileMenu.close')}
              onClick={handleCloseClick}
              data-testid="mobile-nav-drawer-close"
            />
          }
        >
          {drawerTitle}
        </DrawerHeaderTitle>
      </DrawerHeader>
      <DrawerBody className={styles.body}>
        <nav aria-label={t('leftNav.navigationLabel')}>
          <ul className={styles.navList}>
            {items.map((vm) => {
              const Icon = vm.isActive ? vm.item.iconActive : vm.item.icon;
              const testId = `mobile-nav-item-${vm.item.key}`;
              const hasChildren = vm.visibleChildren.length > 0;
              return (
                <li key={vm.item.key}>
                  <a
                    href={vm.item.defaultRoute}
                    className={mergeClasses(styles.navItem, vm.isActive && styles.navItemActive)}
                    data-testid={testId}
                    aria-current={vm.isActive ? 'page' : undefined}
                    onClick={(event) => {
                      event.preventDefault();
                      handleNavigate(vm);
                    }}
                  >
                    <span className={styles.navIcon} aria-hidden="true">
                      <Icon />
                    </span>
                    <span className={styles.navLabel}>{t(vm.item.labelKey)}</span>
                  </a>
                  {hasChildren && (
                    <ul className={styles.navSubList} aria-label={t(vm.item.labelKey)}>
                      {vm.visibleChildren.map((sub) => {
                        const isSubActive = vm.activeChildKey === sub.key;
                        const SubIcon = isSubActive ? sub.iconActive : sub.icon;
                        const subTestId = `mobile-nav-subitem-${sub.key}`;
                        return (
                          <li key={sub.key}>
                            <a
                              href={sub.route}
                              className={mergeClasses(
                                styles.navSubItem,
                                isSubActive && styles.navSubItemActive
                              )}
                              data-testid={subTestId}
                              aria-current={isSubActive ? 'page' : undefined}
                              onClick={(event) => {
                                event.preventDefault();
                                handleNavigateSub(vm.item, sub);
                              }}
                            >
                              <span className={styles.navSubIcon} aria-hidden="true">
                                <SubIcon />
                              </span>
                              <span className={styles.navLabel}>{t(sub.labelKey)}</span>
                            </a>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </li>
              );
            })}
          </ul>
        </nav>
      </DrawerBody>
    </OverlayDrawer>
  );
}

MobileNavDrawer.displayName = 'MobileNavDrawer';
